import { useCallback } from 'react';
import { trace } from '@opentelemetry/api';
import type { NavigateFunction } from 'react-router-dom';
import type { FlowId, SelfServiceData, Step, StepId } from '../../types';

interface UseWizardNavigationParams {
  currentStepId: StepId;
  startStep: StepId;
  resolvedFlowId: FlowId;
  navigate: NavigateFunction;
  locationSearch: string;
  pushHistory: (step: StepId) => void;
  popHistory: () => StepId | null;
  setCurrentStepId: (stepId: StepId) => void;
  showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
  activeSteps: Partial<Record<StepId, Step>>;
  selfServiceData: SelfServiceData;
  navSourceRef: React.MutableRefObject<null | 'programmatic'>;
  lastStepRef: React.MutableRefObject<StepId>;
}

const buildPath = (flowId: FlowId, startStep: StepId, stepId: StepId) => {
  return stepId === startStep ? `/${flowId}` : `/${flowId}/wizard/${stepId}`;
};

export const useWizardNavigation = ({
  currentStepId,
  startStep,
  resolvedFlowId,
  navigate,
  locationSearch,
  pushHistory,
  popHistory,
  setCurrentStepId,
  showToast,
  activeSteps,
  selfServiceData,
  navSourceRef,
  lastStepRef
}: UseWizardNavigationParams) => {
  const goToStep = useCallback((stepId: StepId, pushToHistory = true) => {
    const tracer = trace.getTracer('ui.wizard');
    tracer.startActiveSpan('NavigateStep', span => {
      span.setAttribute('wizard.step.from', currentStepId);
      span.setAttribute('wizard.step.to', stepId);
      span.setAttribute('wizard.transition', `${currentStepId}->${stepId}`);
      try {
        if (pushToHistory) {
          pushHistory(currentStepId);
        }
        const path = buildPath(resolvedFlowId, startStep, stepId);
        navSourceRef.current = 'programmatic';
        navigate({ pathname: path, search: locationSearch }, { replace: !pushToHistory });
        setCurrentStepId(stepId);
        lastStepRef.current = stepId;
      } finally {
        span.end();
      }
    });
  }, [currentStepId, startStep, resolvedFlowId, navigate, locationSearch, pushHistory, setCurrentStepId, navSourceRef, lastStepRef]);

  const goBack = useCallback(() => {
    const previousStep = popHistory();
    navSourceRef.current = 'programmatic';
    if (previousStep) {
      const targetPath = buildPath(resolvedFlowId, startStep, previousStep);
      navigate({ pathname: targetPath, search: locationSearch }, { replace: true });
      setCurrentStepId(previousStep);
      lastStepRef.current = previousStep;
      return;
    }
    navigate({ pathname: `/${resolvedFlowId}`, search: locationSearch });
    setCurrentStepId(startStep);
    lastStepRef.current = startStep;
  }, [popHistory, navigate, locationSearch, resolvedFlowId, startStep, setCurrentStepId, navSourceRef, lastStepRef]);

  const goNext = useCallback(() => {
    const currentStep = activeSteps[currentStepId];
    if (!currentStep) {
      return;
    }
    const error = currentStep.validate?.(selfServiceData) || null;
    if (error) {
      const tracer = trace.getTracer('ui.wizard');
      tracer.startActiveSpan('ValidationError', span => {
        span.setAttribute('wizard.step.id', currentStepId);
        span.setAttribute('validation.error', error);
        span.end();
      });
      showToast(error, 'error');
      return;
    }
    const nextStepId = currentStep.getNext?.(selfServiceData) || currentStep.next;
    if (nextStepId) {
      goToStep(nextStepId as StepId);
    }
  }, [activeSteps, currentStepId, selfServiceData, goToStep, showToast]);

  return { goToStep, goBack, goNext };
};
